'use client'

import * as React from 'react'
import { LazyMotion, domAnimation, m, useInView, useReducedMotion } from 'framer-motion'
import { EASE } from '@/lib/motion'
import { cn } from '@/lib/utils'

// Motion shell for the static WidgetDemoPreview on /widgets/[slug].
// Wraps (does not rewrite) the preview: the whole demo settles in with a
// fade-up, a single lime scan line sweeps the frame once (the same vision
// language as the ai-tagging frame), and the preview's grid tiles
// stagger-reveal then lift on hover. CSS keyframes drive the tile stagger
// so the children stay untouched; the `idk-wdm-play` class only lands
// once the frame is in view, so SSR/no-JS shows the final state.
// Plays once; reduced motion resolves to the final state instantly.

const TILE_STAGGER = `
@keyframes idk-wdm-tile {
  from { opacity: 0; transform: translateY(12px); }
}
.idk-wdm-play ul.grid > li { animation: idk-wdm-tile 0.4s cubic-bezier(0.22, 1, 0.36, 1) backwards; }
.idk-wdm-play ul.grid > li:nth-child(2) { animation-delay: 70ms; }
.idk-wdm-play ul.grid > li:nth-child(3) { animation-delay: 140ms; }
.idk-wdm-play ul.grid > li:nth-child(4) { animation-delay: 210ms; }
.idk-wdm-play ul.grid > li:nth-child(5) { animation-delay: 280ms; }
.idk-wdm-play ul.grid > li:nth-child(n + 6) { animation-delay: 350ms; }
.idk-wdm ul.grid > li { transition: transform 0.2s cubic-bezier(0.22, 1, 0.36, 1); }
@media (hover: hover) {
  .idk-wdm ul.grid > li:hover { transform: translateY(-2px); }
}
@media (prefers-reduced-motion: reduce) {
  .idk-wdm-play ul.grid > li { animation: none; }
  .idk-wdm ul.grid > li:hover { transform: none; }
}
`

export function WidgetDemoMotion({ children, className }: { children: React.ReactNode; className?: string }) {
  const ref = React.useRef<HTMLDivElement>(null)
  const inView = useInView(ref, { once: true, margin: '0px 0px -10% 0px' })
  const reduce = useReducedMotion()
  const play = inView && !reduce

  return (
    <LazyMotion features={domAnimation}>
      <div ref={ref} className={cn('idk-wdm relative', play && 'idk-wdm-play', className)}>
        <style>{TILE_STAGGER}</style>
        <m.div
          initial={reduce ? false : { opacity: 0, y: 24 }}
          animate={inView ? { opacity: 1, y: 0 } : undefined}
          transition={{ duration: 0.5, ease: EASE }}
        >
          {children}
        </m.div>
        {/* One lime scan pass over the demo, then gone. */}
        {play && (
          <div aria-hidden className="pointer-events-none absolute inset-0 z-20 overflow-hidden rounded-2xl">
            <m.div
              className="absolute inset-x-0 h-14"
              style={{
                background:
                  'linear-gradient(to bottom, transparent, hsl(74 100% 66% / 0.18) 70%, hsl(74 100% 66% / 0.85) 99%, transparent)',
              }}
              initial={{ top: '-16%' }}
              animate={{ top: '110%' }}
              transition={{ duration: 1.0, ease: 'linear', delay: 0.35 }}
            />
          </div>
        )}
      </div>
    </LazyMotion>
  )
}
